import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminInvoices, useMarkInvoicePaid, useReportInvoice, useCancelInvoice } from '@/hooks/useAdminInvoices';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, AlertTriangle, Eye, XCircle } from 'lucide-react';
import { format } from 'date-fns';

const AdminInvoices = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [actionDialog, setActionDialog] = useState<{ type: 'paid' | 'report' | 'cancel' | null; invoice: any }>({ type: null, invoice: null });
  const [reportReason, setReportReason] = useState('');
  
  const { data: invoices, isLoading } = useAdminInvoices(filters);
  const { mutate: markPaid } = useMarkInvoicePaid();
  const { mutate: reportInvoice } = useReportInvoice();
  const { mutate: cancelInvoice } = useCancelInvoice();

  const filteredInvoices = invoices?.filter(invoice => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        invoice.document_number.toLowerCase().includes(searchLower) ||
        invoice.shipper_company?.name.toLowerCase().includes(searchLower) ||
        invoice.transporter_company?.name.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleAction = () => {
    if (!actionDialog.invoice) return;
    
    switch (actionDialog.type) {
      case 'paid':
        markPaid(actionDialog.invoice.id);
        break;
      case 'report':
        if (reportReason.trim()) {
          reportInvoice({ invoiceId: actionDialog.invoice.id, reason: reportReason });
          setReportReason('');
        }
        break;
      case 'cancel':
        cancelInvoice(actionDialog.invoice.id);
        break;
    }
    setActionDialog({ type: null, invoice: null });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      draft: { color: 'bg-[#87ADD4]', label: t('billing.statuses.draft') },
      sent: { color: 'bg-[#0880AE]', label: t('billing.statuses.sent') },
      paid: { color: 'bg-[#14A38B]', label: t('admin.invoices.statuses.paid') },
      reported: { color: 'bg-[#FF7171]', label: t('admin.invoices.statuses.reported') },
      cancelled: { color: 'bg-[#5A6B7A]', label: t('admin.invoices.statuses.cancelled') },
    };
    
    const config = statusConfig[status] || { color: 'bg-[#5A6B7A]', label: status };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">{t('admin.invoices.title')}</h1>
          <p className="text-sm text-muted-foreground mt-2">{t('admin.invoices.subtitle')}</p>
        </div>

        <Card className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <SearchInput
              placeholder={t('common.search')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            <Select value={filters.status || undefined} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger>
                <SelectValue placeholder={t('billing.allStatuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sent">{t('admin.invoices.statuses.pending')}</SelectItem>
                <SelectItem value="paid">{t('admin.invoices.statuses.paid')}</SelectItem>
                <SelectItem value="reported">{t('admin.invoices.statuses.reported')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setFilters({ status: '', search: '' })}>
              {t('common.reset')}
            </Button>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.invoices.invoiceId')}</TableHead>
                    <TableHead>{t('admin.invoices.shipper')}</TableHead>
                    <TableHead>{t('admin.invoices.transporter')}</TableHead>
                    <TableHead>{t('admin.invoices.amount')}</TableHead>
                    <TableHead>{t('admin.invoices.status')}</TableHead>
                    <TableHead>{t('admin.invoices.date')}</TableHead>
                    <TableHead className="text-right">{t('admin.invoices.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices?.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.document_number}</TableCell>
                      <TableCell>{invoice.shipper_company?.name || '-'}</TableCell>
                      <TableCell>{invoice.transporter_company?.name || '-'}</TableCell>
                      <TableCell className="font-semibold">{invoice.total_amount} €</TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>{format(new Date(invoice.issued_at), 'dd/MM/yyyy')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedInvoice(invoice)}
                          >
                            <Eye size={16} />
                          </Button>
                          {(invoice.status === 'sent' || invoice.status === 'reported') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ type: 'paid', invoice })}
                            >
                              <CheckCircle size={16} className="text-[#14A38B]" />
                            </Button>
                          )}
                          {invoice.status === 'sent' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setActionDialog({ type: 'report', invoice })}
                              >
                                <AlertTriangle size={16} className="text-[#F2AC57]" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setActionDialog({ type: 'cancel', invoice })}
                              >
                                <XCircle size={16} className="text-[#FF7171]" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Invoice Details Dialog */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.invoices.invoiceDetails.title')}</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Company Information */}
              <div className="grid grid-cols-2 gap-6">
                {/* Shipper Info */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#37699E] border-b pb-2">{t('admin.invoices.invoiceDetails.shipperInfo')}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('admin.users.company')}</p>
                      <p className="font-medium">{selectedInvoice.shipper_company?.name}</p>
                    </div>
                    {selectedInvoice.shipper_company?.email && (
                      <div>
                        <p className="text-muted-foreground">{t('common.email')}</p>
                        <p className="font-medium">{selectedInvoice.shipper_company.email}</p>
                      </div>
                    )}
                    {selectedInvoice.shipper_company?.phone && (
                      <div>
                        <p className="text-muted-foreground">{t('common.phone')}</p>
                        <p className="font-medium">{selectedInvoice.shipper_company.phone}</p>
                      </div>
                    )}
                    {selectedInvoice.shipper_company?.address && (
                      <div>
                        <p className="text-muted-foreground">{t('settings.company.address')}</p>
                        <p className="font-medium">{selectedInvoice.shipper_company.address}</p>
                      </div>
                    )}
                    {selectedInvoice.shipper_company?.siret && (
                      <div>
                        <p className="text-muted-foreground">{t('settings.company.siret')}</p>
                        <p className="font-medium">{selectedInvoice.shipper_company.siret}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Transporter Info */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#37699E] border-b pb-2">{t('admin.invoices.invoiceDetails.transporterInfo')}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('admin.users.company')}</p>
                      <p className="font-medium">{selectedInvoice.transporter_company?.name}</p>
                    </div>
                    {selectedInvoice.transporter_company?.email && (
                      <div>
                        <p className="text-muted-foreground">{t('common.email')}</p>
                        <p className="font-medium">{selectedInvoice.transporter_company.email}</p>
                      </div>
                    )}
                    {selectedInvoice.transporter_company?.phone && (
                      <div>
                        <p className="text-muted-foreground">{t('common.phone')}</p>
                        <p className="font-medium">{selectedInvoice.transporter_company.phone}</p>
                      </div>
                    )}
                    {selectedInvoice.transporter_company?.address && (
                      <div>
                        <p className="text-muted-foreground">{t('settings.company.address')}</p>
                        <p className="font-medium">{selectedInvoice.transporter_company.address}</p>
                      </div>
                    )}
                    {selectedInvoice.transporter_company?.siret && (
                      <div>
                        <p className="text-muted-foreground">{t('settings.company.siret')}</p>
                        <p className="font-medium">{selectedInvoice.transporter_company.siret}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Order Information */}
              {selectedInvoice.order && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#37699E] border-b pb-2">{t('admin.invoices.invoiceDetails.orderInfo')}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('orders.orderNumber')}</p>
                      <p className="font-medium">{selectedInvoice.order.order_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.statusLabel')}</p>
                      <Badge className={
                        selectedInvoice.order.status === 'delivered' ? 'bg-[#14A38B]' :
                        selectedInvoice.order.status === 'in_transit' ? 'bg-[#0880AE]' :
                        selectedInvoice.order.status === 'cancelled' ? 'bg-[#FF7171]' :
                        'bg-[#87ADD4]'
                      }>
                        {t(`orders.statuses.${selectedInvoice.order.status}`)}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.pickupDate')}</p>
                      <p className="font-medium">{format(new Date(selectedInvoice.order.pickup_date), 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.deliveryDate')}</p>
                      <p className="font-medium">{format(new Date(selectedInvoice.order.delivery_date), 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.pickupLocation')}</p>
                      <p className="font-medium">
                        {selectedInvoice.order.pickup_address || 
                         `${selectedInvoice.order.pickup_city || ''}, ${selectedInvoice.order.pickup_country || ''}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.deliveryLocation')}</p>
                      <p className="font-medium">
                        {selectedInvoice.order.delivery_address || 
                         `${selectedInvoice.order.delivery_city || ''}, ${selectedInvoice.order.delivery_country || ''}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.weight')}</p>
                      <p className="font-medium">{selectedInvoice.order.weight_kg} kg</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.trailerType')}</p>
                      <p className="font-medium">{t(`orders.trailerTypes.${selectedInvoice.order.trailer_type}`)}</p>
                    </div>
                    {(selectedInvoice.order.proposed_price || selectedInvoice.order.final_price) && (
                      <div>
                        <p className="text-muted-foreground">{t('orders.price')}</p>
                        <p className="font-medium">
                          {selectedInvoice.order.final_price || selectedInvoice.order.proposed_price} €
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Invoice Details */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-[#37699E] border-b pb-2">{t('billing.detailsTitle')}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('billing.documentNumber')}</p>
                    <p className="font-medium">{selectedInvoice.document_number}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('billing.documentType')}</p>
                    <p className="font-medium">{t(`billing.types.${selectedInvoice.document_type}`)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('admin.invoices.status')}</p>
                    {getStatusBadge(selectedInvoice.status)}
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('billing.amount')}</p>
                    <p className="font-medium">{selectedInvoice.amount.toFixed(2)} €</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('billing.vatRate')}</p>
                    <p className="font-medium">{selectedInvoice.vat_rate}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('billing.vatAmount')}</p>
                    <p className="font-medium">{selectedInvoice.vat_amount.toFixed(2)} €</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('billing.totalAmount')}</p>
                    <p className="font-medium text-lg text-[#37699E]">{selectedInvoice.total_amount.toFixed(2)} €</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('billing.issueDate')}</p>
                    <p className="font-medium">{format(new Date(selectedInvoice.issued_at), 'dd/MM/yyyy HH:mm')}</p>
                  </div>
                  {selectedInvoice.due_date && (
                    <div>
                      <p className="text-muted-foreground">{t('billing.dueDate')}</p>
                      <p className="font-medium">{format(new Date(selectedInvoice.due_date), 'dd/MM/yyyy')}</p>
                    </div>
                  )}
                  {selectedInvoice.paid_at && (
                    <div>
                      <p className="text-muted-foreground">{t('billing.paidDate')}</p>
                      <p className="font-medium text-[#14A38B]">{format(new Date(selectedInvoice.paid_at), 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">{t('common.createdAt')}</p>
                    <p className="font-medium">{format(new Date(selectedInvoice.created_at), 'dd/MM/yyyy HH:mm')}</p>
                  </div>
                </div>
              </div>

              {/* Notes/Comments */}
              {selectedInvoice.notes && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-[#37699E] border-b pb-2">{t('admin.invoices.invoiceDetails.comments')}</h3>
                  <p className="text-sm text-muted-foreground bg-[#EBF4F8] p-3 rounded-lg">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Confirmation */}
      <AlertDialog open={actionDialog.type === 'paid'} onOpenChange={() => setActionDialog({ type: null, invoice: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.invoices.confirmMarkPaid')}</AlertDialogTitle>
            <AlertDialogDescription>{t('admin.invoices.confirmMarkPaidMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction}>{t('common.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Invoice Dialog */}
      <Dialog open={actionDialog.type === 'report'} onOpenChange={() => setActionDialog({ type: null, invoice: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.invoices.confirmReport')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('admin.invoices.confirmReportMessage')}</p>
            <Textarea
              placeholder={t('admin.invoices.reportReason')}
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ type: null, invoice: null })}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAction} disabled={!reportReason.trim()}>
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Confirmation */}
      <AlertDialog open={actionDialog.type === 'cancel'} onOpenChange={() => setActionDialog({ type: null, invoice: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.invoices.confirmCancel')}</AlertDialogTitle>
            <AlertDialogDescription>{t('admin.invoices.confirmCancelMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction}>{t('common.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminInvoices;
